import { type DropDownListViewModel, useGetApiV1BannerPriorityList } from '@/shared/api/generated';
import { unwrapPagedResponse } from '@/shared/utils/unwrapPagedResponse';

/** Opção do select de prioridade. */
export interface BannerPriorityOption {
  readonly value: number;
  readonly label: string;
}

/**
 * Hook com as opções de prioridade do Banner.
 *
 * Consome `GET /api/v1/banner/priority/list` em vez de hardcodar os rótulos de
 * `EBannerPriority`: é o endpoint que existe exatamente para isso e os rótulos
 * vêm traduzidos pelo backend. Se a chamada falhar, `isError` permite ao select
 * ficar desabilitado preservando o valor atual do formulário.
 *
 * @returns `{ options, isLoading, isError, refetch }`.
 *
 * @example
 * ```tsx
 * const { options, isError } = useBannerPriorityOptions();
 * ```
 */
export function useBannerPriorityOptions() {
  const query = useGetApiV1BannerPriorityList();

  // O lookup é declarado como array pelo Kubb, mas o backend deste projeto
  // envelopa listagens de forma inconsistente — iterar direto quebra em runtime
  // (`.filter is not a function`), como já aconteceu em 5 features.
  const options: BannerPriorityOption[] = unwrapPagedResponse<DropDownListViewModel>(query.data)
    .filter(item => item.key !== undefined)
    .map(item => ({ value: item.key as number, label: item.value ?? '' }));

  return {
    options,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  } as const;
}
